import { Link } from "react-router-dom";
import { Button } from "@jambushrushti/components/ui/button";
import { ArrowRight, Scroll, Users, Building2, Image } from "lucide-react";

const teachings = [
  {
    icon: Scroll,
    title: "Sacred Teachings",
    description: "Access Guruvani - profound discourses and written works",
    href: "/guruvani",
  },
  {
    icon: Users,
    title: "Legacy of Pujyapad Muni Jambuvijayaji Maharaj",
    description: "Explore the lineage and contributions of disciples",
    href: "/about/parivar",
  },
  {
    icon: Building2,
    title: "Research Center",
    description: "Digital museum preserving Gurudev's legacy",
    href: "/about/gyan-kendra",
  },
];

export function AboutPreview() {
  return (
    <section className="bg-gradient-to-b from-[#FAF7F2] to-[#F5F0E8] py-10 lg:py-14">
      <div className="container mx-auto px-6">
        <div className="grid items-start gap-10 lg:grid-cols-2 lg:gap-16">
          {/* Left Content Section */}
          <div className="space-y-6">
            <div>
              <p
                className="mb-3 text-sm font-medium tracking-wide text-[#D4A03C]"
                style={{ fontFamily: "Inter, sans-serif" }}
              >
                About Gurudev
              </p>
              <h2
                className="text-3xl font-bold leading-tight text-[#2D2A26] md:text-4xl"
                style={{ fontFamily: "Playfair Display, serif" }}
              >
                Gurudev Muni Jambuvijayaji Maharaj Saheb
              </h2>
            </div>

            <p
              className="text-[15px] leading-relaxed text-[#5A5650]"
              style={{ fontFamily: "Inter, sans-serif" }}
            >
              Muni Jambuvijayaji Maharaj Saheb (1923–2009), also known as
              Gurudev Muni Jambuvijayaji Maharajsaheb, was a monk belonging to
              the Tapa Gaccha order of Shwetambar sect of Jainism. Muni
              Jambuvijayaji was born as Chunilal Bhogilal Joitram in 1923 in
              town of Mandal, Gujarat. His father's name was Bhogilal Mohanlal
              Joitram (1895–1959) and his mother's name was Aniben Popatlal
              (1894–1995). He was born into a deeply religious family.
            </p>

            {/* Quote */}
            <div className="rounded-xl bg-white/80 p-6 shadow-sm backdrop-blur-sm">
              <div className="flex gap-4">
                <div className="font-serif text-4xl leading-none text-[#D4A03C]">
                  "
                </div>
                <div>
                  <p
                    className="text-[15px] italic leading-relaxed text-[#2D2A26]"
                    style={{ fontFamily: "Inter, sans-serif" }}
                  >
                    The pursuit of knowledge is the path to liberation. Through
                    study and contemplation, we illuminate the darkness of
                    ignorance."
                  </p>
                  <p
                    className="mt-3 text-sm font-medium text-[#8B8B8B]"
                    style={{ fontFamily: "Inter, sans-serif" }}
                  >
                    — Gurudev Muni Jambuvijayaji Maharaj Saheb
                  </p>
                </div>
              </div>
            </div>

            {/* CTA Buttons */}
            <div className="grid grid-cols-2 gap-2.5 sm:flex sm:flex-wrap sm:gap-3">
              <Button
                variant="outline"
                size="lg"
                className="h-11 w-full rounded-full border-[#D5D0C5] bg-white px-2.5 text-xs text-[#2D2A26] transition-all hover:border-[#2D2A26] hover:bg-[#2D2A26] hover:text-white sm:h-12 sm:px-6 sm:text-sm"
                style={{ fontFamily: "Inter, sans-serif" }}
                asChild
              >
                <Link to="/about/gurudev" className="justify-center">
                  Read Biography
                  <ArrowRight className="ml-1.5 h-3.5 w-3.5 shrink-0 sm:ml-2 sm:h-4 sm:w-4" />
                </Link>
              </Button>
              <Button
                variant="outline"
                size="lg"
                className="h-11 w-full rounded-full border-[#D5D0C5] bg-white px-2.5 text-xs text-[#2D2A26] transition-all hover:border-[#2D2A26] hover:bg-[#2D2A26] hover:text-white sm:h-12 sm:px-6 sm:text-sm"
                style={{ fontFamily: "Inter, sans-serif" }}
                asChild
              >
                <Link to="/gallery" className="justify-center">
                  <Image className="mr-1.5 h-3.5 w-3.5 shrink-0 sm:mr-2 sm:h-4 sm:w-4" />
                  Photo Gallery
                </Link>
              </Button>
            </div>

            {/* Bottom Pill Links */}
            <div className="flex gap-3 pt-2">
              <Link
                to="/about/parivar"
                className="shadow-xs rounded-full border border-[#D5D0C5] bg-white px-5 py-2.5 text-sm font-medium text-[#2D2A26] transition-all hover:border-[#2D2A26] hover:bg-[#2D2A26] hover:text-white"
                style={{ fontFamily: "Inter, sans-serif" }}
              >
                Legacy
              </Link>
              <Link
                to="/about/gyan-kendra"
                className="shadow-xs rounded-full border border-[#D5D0C5] bg-white px-5 py-2.5 text-sm font-medium text-[#2D2A26] transition-all hover:border-[#2D2A26] hover:bg-[#2D2A26] hover:text-white"
                style={{ fontFamily: "Inter, sans-serif" }}
              >
                Research Center
              </Link>
            </div>
          </div>

          {/* Right Section - Image Grid and Teaching Cards */}
          <div className="space-y-5">
            {/* Image Grid - 3 images in a row */}
            <div className="grid grid-cols-3 gap-3">
              <img
                src="https://siddhijambuparivar.com/wp-content/uploads/2022/11/96-min.jpg"
                alt="Gurudev Muni Jambuvijayaji Maharaj Saheb"
                className="h-52 w-full rounded-xl object-cover object-top"
              />
              <img
                src="https://siddhijambuparivar.com/wp-content/uploads/2022/11/3-min.jpg"
                alt="Gurudev teaching"
                className="h-52 w-full rounded-xl object-cover object-center"
              />
              <img
                src="https://siddhijambuparivar.com/wp-content/uploads/2022/11/38-min.jpg"
                alt="Gurudev with disciples"
                className="h-52 w-full rounded-xl object-cover object-top"
              />
            </div>

            {/* Teaching Cards */}
            <div className="space-y-3">
              {teachings.map(item => {
                const IconComponent = item.icon;
                return (
                  <Link
                    key={item.title}
                    to={item.href}
                    className="group flex items-center gap-4 rounded-xl border border-[#E5E0D5]/50 bg-white/80 p-4 backdrop-blur-sm transition-all duration-300 hover:shadow-md"
                  >
                    <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#FAF7F2]">
                      <IconComponent className="h-5 w-5 text-[#D4A03C]" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <h3
                        className="text-base font-semibold text-[#2D2A26]"
                        style={{ fontFamily: "Playfair Display, serif" }}
                      >
                        {item.title}
                      </h3>
                      <p
                        className="text-sm text-[#5A5650]"
                        style={{ fontFamily: "Inter, sans-serif" }}
                      >
                        {item.description}
                      </p>
                    </div>
                    <ArrowRight className="h-5 w-5 flex-shrink-0 text-[#C4C4C4] transition-all group-hover:translate-x-1" />
                  </Link>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
